import React, { useState, useEffect } from 'react';
import API from '../../services/api.ts';
import { toast } from 'react-toastify';

const AddExistingDatasetModal = ({ show, onHide, projectId, onDatasetAdded }) => {
  const [datasets, setDatasets] = useState([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState('');
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (show) {
      fetchAvailableDatasets();
    }
  }, [show, projectId]);

  const fetchAvailableDatasets = async () => {
    setLoading(true);
    try {
      // Fetch all user's datasets
      const response = await API.get('/datasets/');
      // Filter out datasets that are already in THIS project
      // Datasets can belong to multiple projects, so only exclude if already in current project
      const availableDatasets = response.data.filter(dataset => {
        const projectIds = dataset.projects || [];
        return !projectIds.includes(parseInt(projectId));
      });
      setDatasets(availableDatasets);
    } catch (err) {
      console.error('Error fetching datasets:', err);
      toast.error('Failed to fetch available datasets.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDataset = async () => {
    if (!selectedDatasetId) {
      toast.warning('Please select a dataset.');
      return;
    }

    setProcessing(true);
    try {
      // Use the add-to-project endpoint (supports M2M relationship)
      await API.post(`/datasets/${selectedDatasetId}/add-to-project/`, {
        project_id: projectId
      });
      toast.success('Dataset added to project successfully!');
      setSelectedDatasetId('');
      if (onDatasetAdded) {
        onDatasetAdded();
      }
      onHide();
    } catch (err) {
      console.error('Error adding dataset to project:', err);
      const errorMsg = err.response?.data?.detail || err.response?.data?.error || 'Failed to add dataset to project.';
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setSelectedDatasetId('');
    onHide();
  };

  if (!show) return null;

  return (
    <>
      <div className="modal-backdrop show" onClick={handleClose}></div>
      <div className="modal show" style={{ display: 'block', zIndex: 1050 }} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fa fa-database me-2"></i>
                Add Existing Dataset to Project
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="alert alert-info">
                <i className="fa fa-info-circle me-2"></i>
                Select a dataset to add to this project. Datasets can belong to multiple projects.
              </div>

              {loading ? (
                <div className="text-center py-3">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">Select Dataset</label>
                  <select
                    className="form-select"
                    value={selectedDatasetId}
                    onChange={(e) => setSelectedDatasetId(e.target.value)}
                    disabled={processing}
                  >
                    <option value="">-- Choose a dataset --</option>
                    {datasets.map(dataset => (
                      <option key={dataset.id} value={dataset.id}>
                        {dataset.name} ({dataset.patient_count || 0} patients)
                      </option>
                    ))}
                  </select>
                  {datasets.length === 0 && (
                    <p className="text-muted small mt-2">
                      <i className="fa fa-exclamation-triangle me-1"></i>
                      No datasets available. All your datasets are already in this project.
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={processing}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddDataset}
                disabled={!selectedDatasetId || processing || loading}
              >
                {processing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Adding...
                  </>
                ) : (
                  <>
                    <i className="fa fa-plus me-2"></i>
                    Add Dataset
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddExistingDatasetModal;
